import { useEffect } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import type { AuthController } from "../../api/src/auth/auth.controller";
import type { UnpackResponse } from "../../api/src/lib";
import { useAuthenticatedFetch } from "./lib/api";

/**
 * The services whose OAuth provider redirects back to this page. Redtail and
 * Slant connect through `/integrations/login/:service` instead, so this page
 * posts nothing for them (APP-2183).
 */
export const OAUTH_CALLBACK_SERVICES = ["wealthbox", "schwab"];

const Auth = () => {
  const { service } = useParams();
  const navigate = useNavigate();

  const isAuth0 = service === "auth0";

  useEffect(() => {
    if (!isAuth0 && (window.opener === null || window.opener === window)) {
      navigate("/");
    }
  }, [isAuth0, navigate]);

  const [params] = useSearchParams();
  const code = params.get("code");
  // The `state` the API issued to this user when the connect started. Without
  // it the code could be anyone's, so the page does not post it.
  const state = params.get("state");

  const canAuthorize =
    typeof service !== "undefined" &&
    OAUTH_CALLBACK_SERVICES.includes(service) &&
    code !== null &&
    state !== null &&
    state !== "";

  const { isSuccess, isError } = useAuthenticatedFetch<
    UnpackResponse<AuthController["authorizeService"]>
  >(
    "/auth/authorize",
    {
      method: "POST",
      body: JSON.stringify({
        service: service,
        code,
        state,
      }),
    },
    {
      enabled: canAuthorize,
      retry: false,
    },
    ["/auth/authorize", service, code, state],
  );

  useEffect(() => {
    if (isSuccess) {
      window.opener.location.reload();
      window.opener?.console.debug(`Authorized Service: ${service}`);
      window.close();
    }
  }, [isSuccess, service]);

  if (!isAuth0 && (!canAuthorize || isError)) {
    return (
      <p>
        This connection could not be verified. Close this window and select
        Connect on your profile again.
      </p>
    );
  }

  return <p>Authorizing...</p>;
};

export default Auth;
